import { defineStore } from 'pinia'
import { ref } from 'vue'
import { localStorage, sessionStorage } from '@shared/helpers/storage'
import * as apiRequest from '@shared/api-endpoints/index.js'
import VERIFICATION_METHOD from '@us/app/config/verification-methods.js'
import useLandingPageStore from '@shared/stores/useLandingPageStore.js'

const eligibilitySessionHashKey = 'ELIGIBILITY_ATTEMPT_HASH'
const signupContextLocalStorageKey = 'KWC_SIGNUP_CONTEXT'
const NON_FIELD_KEYS = new Set([
  'corporate_key',
  'slug',
  'landing_page_slug',
  'eligibility_session_hash',
  'corporate_eligibility_group_id',
  'type',
  'eligible_token',
  'voucher',
])

const createEmptySignupContext = () => ({
  webEntryPointUrl: '',
  webEntryPointReferrerUrl: '',
})

const isNonEmptyString = (value) =>
  typeof value === 'string' && value.trim().length > 0

const hasMeaningfulSignupContextData = ({
  webEntryPointUrl,
  webEntryPointReferrerUrl,
}) =>
  isNonEmptyString(webEntryPointUrl) ||
  isNonEmptyString(webEntryPointReferrerUrl)

const isLandingPageSignupContext = (url) =>
  typeof url === 'string' && url.includes('/information')

const shouldReplaceStoredSignupContext = ({
  currentWebEntryPointUrl,
  incomingWebEntryPointUrl,
}) => {
  if (typeof incomingWebEntryPointUrl !== 'string') return false
  if (!incomingWebEntryPointUrl.length) return false
  if (!currentWebEntryPointUrl?.length) return true

  return (
    isLandingPageSignupContext(incomingWebEntryPointUrl) &&
    incomingWebEntryPointUrl !== currentWebEntryPointUrl
  )
}

const parseStoredSignupContext = (payload) => {
  if (Object(payload) !== payload) return createEmptySignupContext()

  return {
    webEntryPointUrl:
      typeof payload.webEntryPointUrl === 'string'
        ? payload.webEntryPointUrl
        : '',
    webEntryPointReferrerUrl:
      typeof payload.webEntryPointReferrerUrl === 'string'
        ? payload.webEntryPointReferrerUrl
        : '',
  }
}

const readSignupContextFromLocalStorage = () => {
  try {
    const payload = localStorage.getItem(signupContextLocalStorageKey)
    if (!payload) return createEmptySignupContext()
    return parseStoredSignupContext(JSON.parse(payload))
  } catch {
    return createEmptySignupContext()
  }
}

const persistSignupContextToLocalStorage = (signupContext) => {
  try {
    localStorage.setItem(
      signupContextLocalStorageKey,
      JSON.stringify(signupContext),
    )
  } catch {
    // local storage may be unavailable in some browsers/tests
  }
}

function extractEligibilityFields(payload) {
  return Object.fromEntries(
    Object.entries(payload)
      .filter(
        ([key, value]) =>
          !NON_FIELD_KEYS.has(key) && value != null && value !== '',
      )
      .map(([key, value]) => [key, value.toString().trim()])
      .filter(([, value]) => value.length > 0),
  )
}

/**
 * returns the eligibility session hash and sets it if it is not set yet
 * @param {string} identifier - a stable identifier (landing page slug or corporate key) to encode in the hash
 * @returns {string} the eligibility session hash
 */
function getOrSetEligibilitySessionHash(identifier) {
  let hash = getEligibilitySessionHash()
  if (hash === null) {
    hash = btoa(`${identifier}${Date.now()}`)
    sessionStorage.setItem(eligibilitySessionHashKey, hash)
  }
  return hash
}

/**
 * @returns {string | null} the eligibility session hash or null if it is not set
 */
export function getEligibilitySessionHash() {
  return sessionStorage.getItem(eligibilitySessionHashKey)
}

export const useUsStore = defineStore(
  'us/app',
  () => {
    // State
    const formData = ref({
      acceptData: false,
      acceptPhoneCall: false,
      acceptTerms: false,
      acceptTracking: false,
      confirmed: false,
      dateOfBirth: '',
      email: '',
      emailCode: '',
      lastName: '',
      firstName: '',
      password: '',
      phoneNumber: '',
      voucherCode: '',
    })

    const signupContext = ref(readSignupContextFromLocalStorage())

    /** @type {import('vue').Ref<{}>} */
    const corporateEligibilityData = ref({})
    /** @type {import('vue').Ref<string>} */
    const voucher = ref(null)

    // Actions
    function updateFormData(data) {
      formData.value = { ...formData.value, ...data }
    }

    function updateSignupContext({
      webEntryPointUrl,
      webEntryPointReferrerUrl,
    } = {}) {
      if (
        !hasMeaningfulSignupContextData({
          webEntryPointUrl,
          webEntryPointReferrerUrl,
        })
      ) {
        return
      }

      if (
        shouldReplaceStoredSignupContext({
          currentWebEntryPointUrl: signupContext.value.webEntryPointUrl,
          incomingWebEntryPointUrl: webEntryPointUrl,
        })
      ) {
        signupContext.value = {
          webEntryPointUrl,
          webEntryPointReferrerUrl: webEntryPointReferrerUrl || '',
        }
      }

      // only update the fields in the signup context if they were not set yet
      signupContext.value.webEntryPointUrl ||= webEntryPointUrl
      signupContext.value.webEntryPointReferrerUrl ||= webEntryPointReferrerUrl

      persistSignupContextToLocalStorage(signupContext.value)
    }

    async function getLandingPageEligibilityConfig(landingPageSlug) {
      const landingPageStore = useLandingPageStore()
      const landingPage =
        await landingPageStore.ensureLandingPage(landingPageSlug)

      return {
        memberIdTitle: landingPage?.memberIdTitle ?? null,
      }
    }

    /**
     * Landing-page owned eligibility verification.
     * Backend selects the correct eligibility list(s) based on the landing page slug.
     */
    async function checkCorporateEligibility(eligibilityData) {
      const landingPageStore = useLandingPageStore()
      const landingPageSlug =
        eligibilityData.landing_page_slug || landingPageStore.landingPage?.slug

      if (!landingPageSlug) {
        throw new Error('Missing landing_page_slug for eligibility check')
      }

      const eligibilitySessionHash =
        getOrSetEligibilitySessionHash(landingPageSlug)

      if (eligibilityData.solera_lookup_key) {
        try {
          await apiRequest.checkCorporateEligibility({
            corporate_key:
              eligibilityData.corporate_key || eligibilityData.slug,
            eligibility_session_hash: eligibilitySessionHash,
            solera_lookup_key: eligibilityData.solera_lookup_key,
          })

          const data = {
            ...eligibilityData,
            // take solera as the corporate_key from the eligibilityData (as backend does not return it in this case)
            eligibility_session_hash: eligibilitySessionHash,
            type: VERIFICATION_METHOD.ELIGIBILITY_LIST,
          }
          corporateEligibilityData.value = data
          return data
        } catch (e) {
          corporateEligibilityData.value = {}
          throw e
        }
      }

      const fields = extractEligibilityFields(eligibilityData)

      try {
        const response = await apiRequest.checkEligibilityWithList({
          landingPageSlug,
          eligibilitySessionHash,
          fields,
        })

        const data = {
          ...fields,
          eligibility_session_hash: eligibilitySessionHash,
          corporate_key: response.data.corporate_key,
          type: VERIFICATION_METHOD.ELIGIBILITY_LIST,
        }
        corporateEligibilityData.value = data
        return data
      } catch (e) {
        corporateEligibilityData.value = {}
        throw e
      }
    }

    async function checkEligibilityWithToken(corporateSlug, eligible_token) {
      const eligibilitySessionHash =
        getOrSetEligibilitySessionHash(corporateSlug)
      const response = await apiRequest.checkEligibilityWithEligibleToken(
        eligibilitySessionHash,
        eligible_token,
      )
      const data = {
        eligibility_session_hash: eligibilitySessionHash,
        eligible_token,
        corporate_key: response.data.corporate_key,
        type: VERIFICATION_METHOD.ELIGIBLE_TOKEN,
      }
      corporateEligibilityData.value = data
      return data
    }

    async function checkVoucher(code) {
      const response = await apiRequest.checkVoucher(code)
      const data = {
        voucher: response.data.corporate_voucher.code,
        corporate_key: response.data.corporate_voucher.corporate_name,
        type: VERIFICATION_METHOD.VOUCHER,
      }
      corporateEligibilityData.value = data
      voucher.value = response.data.corporate_voucher.code
      return data
    }

    return {
      // State
      formData,
      signupContext,
      corporateEligibilityData,
      voucher,
      // Actions
      updateFormData,
      updateSignupContext,
      getLandingPageEligibilityConfig,
      checkCorporateEligibility,
      checkEligibilityWithToken,
      checkVoucher,
    }
  },
  {
    persist: {
      storage: sessionStorage,
    },
  },
)

export default useUsStore
